import React, { useEffect, useState } from 'react';

import { TablePagination } from './TablePagination';
import { TableRenderer } from './TableRenderer';
import { ClientTableProps, DataTableProps, ServerTableProps, TablePresentationProps } from './types';
import { isPaginationEnabled, useDataTable } from './useDataTable';

function DataTableShell<T>(props: DataTableProps<T>) {
    const {
        className = '',
        renderToolbar,
        deferInitialRender = false,
        isLoading,
        isFetching,
        loadingRowCount,
        emptyMessage,
        onRowClick,
        rowClassName,
        truncateHeaders,
        truncateCells,
        renderRowDetails,
        ...tableOptions
    } = props;
    const truncateByDefault = props.mode === 'server';
    const presentation: TablePresentationProps<T> = {
        isLoading,
        isFetching,
        loadingRowCount,
        emptyMessage,
        onRowClick,
        rowClassName,
        truncateHeaders: truncateHeaders ?? truncateByDefault,
        truncateCells: truncateCells ?? truncateByDefault,
        renderRowDetails,
    };
    const [shouldRender, setShouldRender] = useState(!deferInitialRender);
    const table = useDataTable(tableOptions);

    useEffect(() => {
        if (deferInitialRender && !shouldRender) setShouldRender(true);
    }, [deferInitialRender, shouldRender]);

    if (!shouldRender) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
            </div>
        );
    }

    return (
        <>
            {renderToolbar && renderToolbar(table)}
            <div className={className}>
                <TableRenderer table={table} {...presentation} />
                {isPaginationEnabled(tableOptions) && <TablePagination table={table} />}
            </div>
        </>
    );
}

/** Sorts, filters, groups, and optionally paginates the complete dataset in memory. */
export function ClientDataTable<T>(props: ClientTableProps<T>) {
    return <DataTableShell {...props} mode="client" />;
}

/** Renders one server-provided page; sorting, filtering, and pagination are delegated through state handlers. */
export function ServerDataTable<T>(props: ServerTableProps<T>) {
    return <DataTableShell {...props} mode="server" />;
}

export type { ClientTableProps, ServerTableProps } from './types';
